import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { useUser } from "@/zustand/store";
import { useSheetStore } from "@/zustand/sheetStore";
import { Edit, Plus } from "lucide-react";
import { useState } from "react";
import { useGetAuthorizedOTP, useUpdateUser } from "@/hooks/use-mutations";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useQueryClient } from "@tanstack/react-query";
import ResendOtpButton, {
  otpAltActionBtn,
} from "@/components/resend-otp-button";
import Throbber from "@/components/throbber";

const EditProfile = ({
  step,
  setStep,
  closeSheet,
}: {
  step: number;
  setStep: (step: number) => void;
  closeSheet: () => void;
}) => {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { openSheet } = useSheetStore();
  const form = useForm({
    defaultValues: {
      firstName: user?.firstName,
      lastName: user?.lastName,
      email: user?.email,
      contact: user.contact,
      otp: "",
    },
  });
  const { mutateAsync: getOtp, isPending: isGettingOtp } =
    useGetAuthorizedOTP();
  const { mutateAsync: updateProfile, isPending: isUpdating } = useUpdateUser();
  const canUseAuthenticator = user?.twoFactorAuthMethod === "authenticator";
  const [otpMethod, setOtpMethod] = useState<TwoFactorMethod>("email");
  const [isSwitchingMethod, setIsSwitchingMethod] = useState(false);
  const [editingFields, setEditingFields] = useState(new Set<string>());
  const toggleEditing = (fieldName: string) => {
    setEditingFields((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(fieldName)) {
        newSet.delete(fieldName);
      } else {
        newSet.add(fieldName);
      }
      return newSet;
    });
  };

  const onSubmit = async (data: any) => {
    try {
      await updateProfile({ ...data, otpMethod });
      queryClient.invalidateQueries({ queryKey: ["user"] });
      closeSheet();
    } catch {
      // ignore
    }
  };

  const handleSwitchOtpMethod = async () => {
    if (otpMethod === "authenticator") {
      setIsSwitchingMethod(true);
      try {
        await getOtp({
          emailAddress: form.getValues("email"),
          purpose: "update",
        });
        form.setValue("otp", "");
        setOtpMethod("email");
      } catch {
        // The mutation's own onError already toasts.
      } finally {
        setIsSwitchingMethod(false);
      }
    } else {
      form.setValue("otp", "");
      setOtpMethod("authenticator");
    }
  };
  return (
    <div className="text-dark-text-50">
      <h1>Edit Profile</h1>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          {step === 1 && (
            <>
              <div className="mt-4 space-y-4">
                <div className="flex flex-col gap-2.5 *:grow lg:flex-row">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-normal">
                          First Name
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              readOnly={!editingFields.has("firstName")}
                              className="field-gradient-aux-4 text-dark-text-50 placeholder:text-dark-text-300 rounded pr-4"
                              {...field}
                            />
                            <Button
                              variant={"ghost"}
                              type="button"
                              className="absolute top-1/2 right-2 size-6 -translate-y-1/2 rounded"
                              onClick={() => toggleEditing("firstName")}
                            >
                              <Edit size={16} strokeWidth={1.5} />
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-sm font-normal">
                          Last Name
                        </FormLabel>
                        <FormControl>
                          <div className="relative">
                            <Input
                              readOnly={!editingFields.has("lastName")}
                              className="field-gradient-aux-4 text-dark-text-50 placeholder:text-dark-text-300 rounded pr-4"
                              {...field}
                            />
                            <Button
                              variant={"ghost"}
                              type="button"
                              className="absolute top-1/2 right-2 size-6 -translate-y-1/2 rounded"
                              onClick={() => toggleEditing("lastName")}
                            >
                              <Edit size={16} strokeWidth={1.5} />
                            </Button>
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-normal">
                        Email
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            readOnly={!editingFields.has("email")}
                            className="field-gradient-aux-4 text-dark-text-50 placeholder:text-dark-text-300 rounded pr-4"
                            {...field}
                            onChange={(e) => {
                              const cleaned = e.target.value
                                .replace(/\s+/g, "")
                                .toLowerCase();
                              field.onChange(cleaned);
                            }}
                          />
                          <Button
                            variant={"ghost"}
                            type="button"
                            className="absolute top-1/2 right-2 size-6 -translate-y-1/2 rounded"
                            onClick={() => toggleEditing("email")}
                          >
                            <Edit size={16} strokeWidth={1.5} />
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="contact"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-normal">
                        Contact
                      </FormLabel>
                      <FormControl>
                        <div className="flex items-center gap-2">
                          <Input
                            className="field-gradient-aux-4 text-dark-text-50 placeholder:text-dark-text-300 rounded"
                            placeholder={
                              field.value ? field.value : "Add Contact"
                            }
                            {...field}
                          />
                          <Button
                            variant={"outline"}
                            className="bg-transparent"
                            onClick={() => {
                              openSheet(
                                "addContact",
                                2,
                                { contact: field.value },
                                false,
                              );
                            }}
                          >
                            {field.value ? (
                              <Edit size={16} strokeWidth={1.5} />
                            ) : (
                              <Plus size={16} strokeWidth={1.5} />
                            )}
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-sm font-normal">
                        Password
                      </FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type="password"
                            readOnly={!editingFields.has("password")}
                            className="field-gradient-aux-4 text-dark-text-50 placeholder:text-dark-text-300 rounded pr-4"
                            placeholder="••••••••"
                            {...field}
                          />
                          <Button
                            variant={"ghost"}
                            type="button"
                            className="absolute top-1/2 right-2 size-6 -translate-y-1/2 rounded"
                            onClick={() => toggleEditing("password")}
                          >
                            <Edit size={16} strokeWidth={1.5} />
                          </Button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <Button
                disabled={!form.formState.isDirty}
                type="button"
                onClick={async () => {
                  if (canUseAuthenticator) {
                    // Authenticator codes aren't "sent" — skip the OTP
                    // request and let the user enter a code they've
                    // already got.
                    setOtpMethod("authenticator");
                    setStep(2);
                    return;
                  }
                  try {
                    await getOtp({
                      emailAddress: form.getValues("email"),
                      purpose: "update",
                    });
                    setOtpMethod("email");
                    setStep(2);
                  } catch {
                    // The mutation's own onError already toasts.
                  }
                }}
                className="text-dark-text-400 bg-dark-primary-main hover:bg-dark-primary-main/80 mt-8 w-full font-medium"
                isLoading={isGettingOtp}
              >
                Continue
              </Button>
            </>
          )}

          {/* Step 2 */}
          {step === 2 && (
            <>
              {otpMethod === "authenticator" ? (
                <>
                  <h1 className="mt-6 text-2xl font-semibold">
                    2 Factor Authentication
                  </h1>
                  <p className="text-dark-text-300 text-sm leading-3.5">
                    Enter the 6-digit code from your authenticator app to
                    continue.
                  </p>
                </>
              ) : (
                <>
                  <h1 className="mt-6 text-2xl font-semibold">
                    Verification Code
                  </h1>
                  <p className="text-dark-text-300 text-sm leading-3.5">
                    We’ve sent a 6-digit code to your email, enter the code
                    below to verify
                  </p>
                </>
              )}

              <div className="mt-6 flex w-full flex-col gap-2.5">
                <FormField
                  control={form.control}
                  name="otp"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Enter verification code</FormLabel>
                      <FormControl>
                        <InputOTP
                          autoFocus
                          autoComplete="off"
                          pattern={REGEXP_ONLY_DIGITS}
                          maxLength={6}
                          {...field}
                        >
                          <InputOTPGroup className="*:bg-dark-card-5 *:border-dark-stroke-5 gap-3 *:!rounded *:border *:shadow-none">
                            <InputOTPSlot index={0} />
                            <InputOTPSlot index={1} />
                            <InputOTPSlot index={2} />
                            <InputOTPSlot index={3} />
                            <InputOTPSlot index={4} />
                            <InputOTPSlot index={5} />
                          </InputOTPGroup>
                        </InputOTP>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {otpMethod === "email" && (
                  <ResendOtpButton
                    onResend={() =>
                      getOtp({
                        emailAddress: form.getValues("email"),
                        purpose: "update",
                      })
                    }
                  />
                )}

                <Button
                  disabled={isUpdating}
                  className="text-dark-text-400 bg-dark-primary-main hover:bg-dark-primary-main/80 mt-6 h-11 w-full"
                  type="submit"
                  isLoading={isUpdating}
                >
                  Save
                </Button>

                {canUseAuthenticator && (
                  <button
                    type="button"
                    onClick={handleSwitchOtpMethod}
                    disabled={isSwitchingMethod}
                    className={otpAltActionBtn}
                  >
                    {isSwitchingMethod ? (
                      <Throbber />
                    ) : otpMethod === "authenticator" ? (
                      "Use email OTP instead"
                    ) : (
                      "Use authenticator app instead"
                    )}
                  </button>
                )}
              </div>
            </>
          )}
        </form>
      </Form>
    </div>
  );
};

export default EditProfile;
